import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Card,
  Button,
  Space,
  Typography,
  Alert,
  List,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Progress,
  Divider,
  message,
  Spin,
  Badge,
  Tooltip,
} from 'antd';
import {
  ScanOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  MobileOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { 
  BarcodeScanner, 
  ScanSession, 
  BarcodeScans, 
  ScanSessionType,
  Product,
  Location 
} from '../../types';

const { Title, Text } = Typography;
const { Option } = Select;

interface BarcodeScannerProps {
  visible: boolean;
  onClose: () => void;
  scanners: BarcodeScanner[];
  sessionType: ScanSessionType;
  warehouseId: string;
  onScanComplete: (scans: BarcodeScans[]) => void;
}

export const BarcodeScannerComponent: React.FC<BarcodeScannerProps> = ({
  visible,
  onClose,
  scanners,
  sessionType,
  warehouseId,
  onScanComplete,
}) => {
  const [selectedScanner, setSelectedScanner] = useState<string>('');
  const [scanSession, setScanSession] = useState<ScanSession | null>(null);
  const [scans, setScans] = useState<BarcodeScans[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanMode, setScanMode] = useState<'auto' | 'manual'>('auto');
  const [loading, setLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Mock data for demonstration
  const mockProducts: Record<string, Product> = {
    '1234567890123': {
      id: '1',
      sku: 'LAP-DELL-XPS13',
      barcode: '1234567890123',
      name: 'Dell XPS 13 Laptop',
      description: 'High-performance ultrabook',
      category: { id: '1', name: 'Electronics', isActive: true },
      brand: 'Dell',
      unitOfMeasure: 'Each',
      costPrice: 999.99,
      sellingPrice: 1299.99,
      minStockLevel: 10,
      maxStockLevel: 100,
      reorderPoint: 20,
      reorderQuantity: 50,
      isActive: true,
      isTrackable: true,
      expiryTracking: false,
      batchTracking: false,
      serialTracking: true,
      suppliers: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  };

  const mockLocations: Record<string, Location> = {
    'A-01-01-01': {
      id: '1',
      code: 'A-01-01-01',
      barcode: 'LOC-A010101',
      type: 'pick',
      capacity: 100,
      currentStock: 45,
      zone: 'A',
      aisle: '01',
      rack: '01',
      shelf: '01',
      isActive: true,
    },
  };

  useEffect(() => {
    if (visible && scanMode === 'auto') {
      initializeCamera();
    }
    return () => {
      stopCamera();
    };
  }, [visible, scanMode]);

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      message.error('Unable to access camera. Please use manual input or check permissions.');
      setScanMode('manual');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const startScanSession = async () => {
    if (!selectedScanner) {
      message.error('Please select a scanner device');
      return;
    }

    setLoading(true);
    try {
      // Mock API call to start scan session
      const newSession: ScanSession = {
        id: Date.now().toString(),
        sessionType,
        scannerId: selectedScanner,
        userId: 'current-user-id',
        warehouseId,
        startTime: new Date(),
        status: 'active',
        scans: [],
        totalScans: 0,
        successfulScans: 0,
        errorScans: 0,
      };
      
      setScanSession(newSession);
      setIsScanning(true);
      message.success('Scan session started successfully');
    } catch (error) {
      message.error('Failed to start scan session');
    } finally {
      setLoading(false);
    }
  };

  const stopScanSession = async () => {
    if (!scanSession) return;

    setLoading(true);
    try {
      const updatedSession = {
        ...scanSession,
        endTime: new Date(),
        status: 'completed' as const,
        totalScans: scans.length,
        successfulScans: scans.filter(s => s.isValid).length,
        errorScans: scans.filter(s => !s.isValid).length,
      };
      
      setScanSession(updatedSession);
      setIsScanning(false);
      onScanComplete(scans);
      message.success('Scan session completed');
    } catch (error) {
      message.error('Failed to stop scan session');
    } finally {
      setLoading(false);
    }
  };

  const processScan = (barcode: string) => {
    if (!scanSession) return;

    const newScan: BarcodeScans = {
      id: Date.now().toString(),
      barcode,
      scannedAt: new Date(),
      isValid: false,
      action: getActionForSessionType(sessionType),
      metadata: {},
    };

    // Validate barcode
    if (mockProducts[barcode]) {
      newScan.isValid = true;
      newScan.productId = mockProducts[barcode].id;
      message.success(`Product found: ${mockProducts[barcode].name}`);
    } else if (mockLocations[barcode] || barcode.startsWith('LOC-')) {
      newScan.isValid = true;
      newScan.locationId = mockLocations[barcode]?.id || 'unknown';
      message.success(`Location scanned: ${barcode}`);
    } else {
      newScan.isValid = false;
      newScan.errorMessage = 'Barcode not found in system';
      message.error('Barcode not recognized');
    }

    setScans(prev => [newScan, ...prev]);
    setManualBarcode('');
  };

  const getActionForSessionType = (type: ScanSessionType) => {
    const actionMap: Record<ScanSessionType, any> = {
      receiving: 'receive_confirm',
      picking: 'pick_confirm',
      cycle_count: 'count_update',
      putaway: 'putaway_confirm',
      shipping: 'ship_confirm',
      transfer: 'product_lookup',
      adjustment: 'product_lookup',
    };
    return actionMap[type] || 'product_lookup';
  };

  const handleManualScan = () => {
    if (!manualBarcode.trim()) {
      message.error('Please enter a barcode');
      return;
    }
    processScan(manualBarcode.trim());
  };

  const getSessionTypeLabel = (type: ScanSessionType) => {
    const labels: Record<ScanSessionType, string> = {
      receiving: 'Receiving',
      picking: 'Picking',
      cycle_count: 'Cycle Count',
      putaway: 'Put Away',
      shipping: 'Shipping',
      transfer: 'Transfer',
      adjustment: 'Adjustment',
    };
    return labels[type] || type;
  };

  const getScannerStatusIcon = (scanner: BarcodeScanner) => {
    if (!scanner.isActive) return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  };

  const activeScanners = scanners.filter(s => s.isActive);
  const successRate = scans.length > 0 ? (scans.filter(s => s.isValid).length / scans.length) * 100 : 0;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ScanOutlined />
          <span>Barcode Scanner - {getSessionTypeLabel(sessionType)}</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Scanner Selection */}
        <Card title="Scanner Setup" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Select Scanner Device:</Text>
                <Select
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="Choose a scanner"
                  value={selectedScanner}
                  onChange={setSelectedScanner}
                >
                  {activeScanners.map(scanner => (
                    <Option key={scanner.id} value={scanner.id}>
                      <Space>
                        {getScannerStatusIcon(scanner)}
                        <span>{scanner.name}</span>
                        <Tag color="blue">{scanner.type}</Tag>
                                                 {scanner.batteryLevel && (
                           <Tooltip title={`Battery: ${scanner.batteryLevel}%`}>
                             <ThunderboltOutlined style={{ 
                               color: scanner.batteryLevel < 20 ? '#ff4d4f' : '#52c41a' 
                             }} />
                           </Tooltip>
                         )}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Scan Mode:</Text>
                <Select
                  style={{ width: '100%', marginTop: '8px' }}
                  value={scanMode}
                  onChange={setScanMode}
                >
                  <Option value="auto">
                    <Space>
                      <CameraOutlined />
                      Camera Scanning
                    </Space>
                  </Option>
                  <Option value="manual">
                    <Space>
                      <MobileOutlined />
                      Manual Input
                    </Space>
                  </Option>
                </Select>
              </div>
            </Col>
          </Row>

          <Space>
            {!isScanning ? (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={startScanSession}
                loading={loading}
                disabled={!selectedScanner}
              >
                Start Session
              </Button>
            ) : (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={stopScanSession}
                loading={loading}
              >
                Stop Session
              </Button>
            )}
            <Button icon={<SettingOutlined />}>
              Scanner Settings
            </Button>
          </Space>
        </Card>

        {/* Session Status */}
        {scanSession && (
          <Card title="Session Status" style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Total Scans"
                  value={scans.length}
                  prefix={<ScanOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Success Rate"
                  value={successRate}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: successRate > 90 ? '#52c41a' : successRate > 70 ? '#faad14' : '#ff4d4f' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Valid Scans"
                  value={scans.filter(s => s.isValid).length}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Errors"
                  value={scans.filter(s => !s.isValid).length}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* Scanning Interface */}
        {isScanning && (
          <Card title="Scanning Interface" style={{ marginBottom: '16px' }}>
            {scanMode === 'auto' ? (
              <div style={{ textAlign: 'center' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: '300px',
                    border: '2px solid #1890ff',
                    borderRadius: '8px',
                  }}
                />
                <div style={{ marginTop: '16px' }}>
                  <Alert
                    message="Point camera at barcode to scan"
                    type="info"
                    showIcon
                  />
                </div>
              </div>
            ) : (
              <div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="Enter barcode manually"
                    value={manualBarcode}
                    onChange={(e) => setManualBarcode(e.target.value)}
                    onPressEnter={handleManualScan}
                    size="large"
                  />
                  <Button
                    type="primary"
                    icon={<ScanOutlined />}
                    onClick={handleManualScan}
                    size="large"
                  >
                    Scan
                  </Button>
                </Space.Compact>
              </div>
            )}
          </Card>
        )}

        {/* Scan Results */}
        {scans.length > 0 && (
          <Card title="Scan Results" extra={<Button icon={<ReloadOutlined />}>Clear</Button>}>
            <List
              dataSource={scans}
              renderItem={(scan) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Badge
                        status={scan.isValid ? 'success' : 'error'}
                        dot
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: scan.isValid ? '#f6ffed' : '#fff2f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {scan.isValid ? (
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                          )}
                        </div>
                      </Badge>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontFamily: 'monospace' }}>{scan.barcode}</span>
                        <Tag color={scan.isValid ? 'green' : 'red'}>
                          {scan.isValid ? 'VALID' : 'ERROR'}
                        </Tag>
                      </div>
                    }
                    description={
                      <div>
                        {scan.productId && mockProducts[scan.barcode] && (
                          <div>Product: {mockProducts[scan.barcode].name}</div>
                        )}
                        {scan.locationId && (
                          <div>Location: {scan.barcode}</div>
                        )}
                        {scan.errorMessage && (
                          <div style={{ color: '#ff4d4f' }}>Error: {scan.errorMessage}</div>
                        )}
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {scan.scannedAt.toLocaleTimeString()}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        )}
      </div>
    </Modal>
  );
}; 